import {
  createContext,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { DEFAULT_STAFF_PERCENT, clampStaffPercent } from "./staffPercent";
import { api } from "../api/client";
import type { Branch, DateRange, GovernorateNode, PlaceFilter } from "../types";
import { daysAgo, isoDate } from "./format";
import { urlParam } from "./urlState";
import { useAsync } from "./useAsync";

export const NO_PLACE: PlaceFilter = { governorate: null, area: null, branchIds: [] };

/** Stores that can be compared at once — one chart series each. */
export const MAX_COMPARE = 6;

/** The default window: the last 90 days (today included). Matches
 *  DEFAULT_RANGE_DAYS in the analytics router. */
export const DEFAULT_RANGE_DAYS = 89;

/** The `branch` URL param: one id, or several comma-joined when comparing.
 *  Ids are slugs, so the comma is safe. */
export const parseBranchParam = (value: string | null): string[] =>
  value ? value.split(",").filter(Boolean) : [];

interface FilterState {
  branches: Branch[];
  branchesError: string | null;
  reloadBranches: () => void;
  governorates: GovernorateNode[];
  place: PlaceFilter;
  setPlace: (place: PlaceFilter) => void;
  /** Branches matching the current governorate/area (ignores branchIds). */
  branchesInPlace: Branch[];
  range: DateRange;
  setRange: (range: DateRange) => void;
  /** Whether demo/test stores are included in analytics (default: excluded). */
  includeDemo: boolean;
  setIncludeDemo: (include: boolean) => void;
  /** True when any demo branch exists — hides the toggle otherwise. */
  hasDemo: boolean;
  /** Whether enrolled staff are taken out of every count (default: yes).
   *  The API always returns everyone plus the staff twins; `lib/staff.ts`
   *  subtracts them when this is on. */
  excludeStaff: boolean;
  setExcludeStaff: (exclude: boolean) => void;
  /** The staff percentage, 0–50: every count is shown with this percentage
   *  taken off (`lib/staffPercent.ts`). Console only. */
  staffPercent: number;
  setStaffPercent: (percent: number) => void;
  /** On a branch PC: the one store this console is. Every view is pinned to
   *  it and the place controls are gone. Null on the hub. */
  defaultBranch: DefaultBranch | null;
  single: boolean;
}

export interface DefaultBranch {
  id: string;
  governorate: string;
  area: string;
}

const FilterContext = createContext<FilterState | null>(null);

interface FilterProviderProps {
  children: ReactNode;
  /** On a branch PC: the store this PC is. Every analytics view is pinned to
   *  it — the URL's place params are ignored. */
  defaultBranch?: DefaultBranch | null;
}

export function FilterProvider({ children, defaultBranch = null }: FilterProviderProps) {
  // Deep links win over defaults; the pages that render these filters keep
  // the params in sync afterwards (lib/urlState.ts).
  const pinned: PlaceFilter | null = defaultBranch
    ? {
        governorate: defaultBranch.governorate,
        area: defaultBranch.area || null,
        branchIds: [defaultBranch.id],
      }
    : null;
  const [place, setPlaceState] = useState<PlaceFilter>(() => {
    if (pinned) return pinned;
    return {
      governorate: urlParam("gov") || null,
      area: urlParam("area") || null,
      branchIds: parseBranchParam(urlParam("branch")),
    };
  });
  // A single-store console cannot be steered to another place.
  const setPlace = (next: PlaceFilter) => setPlaceState(pinned ?? next);
  const [range, setRange] = useState<DateRange>(() => ({
    from: urlParam("from") || daysAgo(DEFAULT_RANGE_DAYS),
    to: urlParam("to") || isoDate(new Date()),
  }));
  const [includeDemo, setIncludeDemoState] = useState<boolean>(() => {
    const fromUrl = urlParam("demo");
    if (fromUrl !== null) return fromUrl === "1";
    return localStorage.getItem("footfall.includeDemo") === "true";
  });
  // Staff Face ID Exclusion: off by default, so the staff percentage below is
  // the only staff adjustment unless the operator turns it on. Its own key —
  // the old "includeStaff" preference defaulted the other way.
  const [excludeStaff, setExcludeStaffState] = useState<boolean>(() => {
    const fromUrl = urlParam("faceid");
    if (fromUrl !== null) return fromUrl === "1";
    try {
      return localStorage.getItem("footfall.staffFaceIdExclusion") === "true";
    } catch {
      return false;
    }
  });
  const setExcludeStaff = (exclude: boolean) => {
    try {
      localStorage.setItem("footfall.staffFaceIdExclusion", String(exclude));
    } catch {
      // Not remembered; the URL still carries it.
    }
    setExcludeStaffState(exclude);
  };
  // Deliberately not remembered — neither in the URL nor in localStorage:
  // every visit starts at the default.
  const [staffPercent, setStaffPercentState] = useState<number>(DEFAULT_STAFF_PERCENT);
  const setStaffPercent = (percent: number) => setStaffPercentState(clampStaffPercent(percent));
  const branchQuery = useAsync(() => api.listBranches(), []);
  const treeQuery = useAsync(() => api.listGovernorates(), []);

  const reload = () => {
    branchQuery.reload();
    treeQuery.reload();
  };

  const value = useMemo<FilterState>(() => {
    const branches = branchQuery.data ?? [];
    const branchesInPlace = branches.filter(
      (b) =>
        (!place.governorate || b.governorate === place.governorate) &&
        (!place.area || b.area === place.area),
    );
    const setIncludeDemo = (include: boolean) => {
      localStorage.setItem("footfall.includeDemo", String(include));
      setIncludeDemoState(include);
      // Turning demo off while a demo place is selected would leave the
      // filter pointing at something the drill-down no longer shows.
      if (!include) {
        const selectedDemo =
          place.branchIds.some(
            (id) => branches.find((b) => b.id === id)?.is_demo,
          ) ||
          (place.governorate &&
            branchesInPlace.length > 0 &&
            branchesInPlace.every((b) => b.is_demo));
        if (selectedDemo) setPlace(NO_PLACE);
      }
    };
    return {
      branches,
      branchesError: branchQuery.error,
      reloadBranches: reload,
      governorates: treeQuery.data ?? [],
      place,
      setPlace,
      branchesInPlace,
      range,
      setRange,
      includeDemo,
      setIncludeDemo,
      hasDemo: branches.some((b) => b.is_demo),
      excludeStaff,
      setExcludeStaff,
      staffPercent,
      setStaffPercent,
      defaultBranch,
      single: defaultBranch !== null,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branchQuery.data, branchQuery.error, treeQuery.data, place, range, includeDemo, excludeStaff, staffPercent, defaultBranch]);

  return (
    <FilterContext.Provider value={value}>{children}</FilterContext.Provider>
  );
}

export function useFilters(): FilterState {
  const context = useContext(FilterContext);
  if (!context) throw new Error("useFilters must be used inside FilterProvider");
  return context;
}

/** Stable string for effect dependency lists. */
export const placeKey = (place: PlaceFilter): string =>
  `${place.governorate ?? ""}|${place.area ?? ""}|${place.branchIds.join(",")}`;

/** Short human label, e.g. "Cairo › Maadi Street 9" or "All branches". */
export function placeLabel(place: PlaceFilter, branches: Branch[]): string {
  if (place.branchIds.length === 1) {
    const b = branches.find((x) => x.id === place.branchIds[0]);
    if (b) return `${b.governorate} › ${b.area} › ${b.name}`;
  }
  if (place.branchIds.length > 1) return `${place.branchIds.length} branches`;
  if (place.area) return `${place.governorate} › ${place.area}`;
  if (place.governorate) return place.governorate;
  return "All branches";
}
